import { PayloadAction } from '@reduxjs/toolkit';
import { isEqual, isNumber, omit, pick } from 'lodash-es';

import { SYNC_WITH_STORAGE_ACTION } from 'state/global-state/global-actions';
import { createAppSlice } from 'state/global-state/utils';
import {
  AvailableRewards,
  IAppliedRewards,
  IRemoveAppliedRewardsAction,
  IResetRewardStateAction,
  IRewardAction,
  IncentiveEvaluationMap,
  LoyaltyUser,
  RewardRedemptionMethod,
} from 'state/loyalty/hooks/types';
import { LoyaltyEngineRewards } from 'state/loyalty/types';

import { userSlice } from '../user/user.slice';

import { IRewardsState } from './rewards.types';
import {
  computeTotalTimesRewardApplied,
  getAppliedPointsInCart,
  getAppliedRewardsFromStorage,
  getStagedCartPoints,
  setStateFromAppliedRewards,
  sumAppliedTimesApplied,
  updateAppliedRewardsInStorage,
} from './rewards.utils';

interface IResolvedRewardApplication {
  id: string;
  cmsId?: string;
  rewardBenefitId: string;
  pointCost: number;
  limitPerOrder?: number | null;
}

type ApplyRewardTarget =
  | (IRewardAction & { reward?: never })
  | {
      cartId: string;
      reward: IResolvedRewardApplication;
      quantity?: number;
    };

type ApplyRewardPayload = ApplyRewardTarget & {
  redemptionMethod: RewardRedemptionMethod;
  enableRewardUnstacking: boolean;
  /** Distinct reward slots when unstacking; defaults to 1 (legacy single-reward behavior). */
  rewardRedemptionLimit?: number;
  loyaltyUser?: LoyaltyUser;
};

export const initialState: IRewardsState = {
  appliedLoyaltyRewards: {},
  availableLoyaltyRewardsMap: {},
  isPricingRewardApplication: false,
  stagedCartPoints: 0,
  shouldRefetchRewards: false,
  rewardsFeedbackMap: {},
};

export const rewardsSlice = createAppSlice({
  name: 'rewards',
  initialState,
  reducers: {
    applyReward: (state, { payload }: PayloadAction<ApplyRewardPayload>) => {
      const {
        cartId,
        redemptionMethod = RewardRedemptionMethod.MOBILE,
        quantity = 1,
        enableRewardUnstacking,
        rewardRedemptionLimit: rewardRedemptionLimitRaw,
      } = payload;
      const resolvedReward = 'reward' in payload ? payload.reward : undefined;
      const rewardBenefitId = resolvedReward
        ? resolvedReward.rewardBenefitId
        : 'rewardBenefitId' in payload
        ? payload.rewardBenefitId
        : '';
      const rewardRedemptionLimit = Math.max(1, rewardRedemptionLimitRaw ?? 1);
      const { appliedLoyaltyRewards, availableLoyaltyRewardsMap, stagedCartPoints } = state;
      const mappedReward = resolvedReward
        ? {
            id: resolvedReward.id,
            sanityId: resolvedReward.cmsId,
            rewardBenefitId: resolvedReward.rewardBenefitId,
            pointCost: resolvedReward.pointCost,
            limitPerOrder: resolvedReward.limitPerOrder,
          }
        : availableLoyaltyRewardsMap[rewardBenefitId];

      if (!mappedReward) {
        return;
      }

      const isApplicable = canApplyReward({
        reward: mappedReward,
        appliedLoyaltyRewards,
        cartId,
        quantity,
        stagedCartPoints,
        enableRewardUnstacking,
        rewardRedemptionLimit,
      });
      if (!isApplicable) {
        return;
      }

      const { pointCost, id: rewardId, sanityId } = mappedReward;

      const newReward = {
        rewardId,
        pointCost,
        rewardBenefitId,
        ...(sanityId ? { sanityId } : null),
        redemptionMethod,
      };

      if (enableRewardUnstacking) {
        let workingApplied: IAppliedRewards = { ...appliedLoyaltyRewards };
        let pointsToRestore = 0;

        const previousAtCartId = workingApplied[cartId];
        if (previousAtCartId) {
          if (previousAtCartId.rewardId === newReward.rewardId) {
            // Same reward incremented on the same cartId (e.g. in-restaurant incrementor).
            // Only deduct the delta cost and accumulate timesApplied — do not restore the
            // previous cost, which would result in a net-zero point deduction.
            // Guard: the total-applications cap must not be exceeded by this increment.
            if (sumAppliedTimesApplied(workingApplied) + quantity > rewardRedemptionLimit) {
              return;
            }
            state.stagedCartPoints = stagedCartPoints - pointCost * quantity;
            const updatedAppliedRewards: IAppliedRewards = {
              ...workingApplied,
              [cartId]: {
                ...newReward,
                timesApplied: previousAtCartId.timesApplied + quantity,
              },
            };
            updateAppliedRewardsInStorage(updatedAppliedRewards);
            state.appliedLoyaltyRewards = updatedAppliedRewards;
            return;
          }
          // Different reward replacing the same cartId slot: restore the previous reward's cost.
          pointsToRestore += previousAtCartId.pointCost * previousAtCartId.timesApplied;
        } else {
          // New slot: evict one application from the oldest entry to stay within the cap.
          // Prefer partial-decrement (timesApplied > 1) over full removal so that other
          // applications on the same cart line are preserved.
          if (sumAppliedTimesApplied(workingApplied) >= rewardRedemptionLimit) {
            const evictKey = Object.keys(workingApplied)[0];
            const evicted = workingApplied[evictKey];
            if (evicted) {
              pointsToRestore += evicted.pointCost;
              if (evicted.timesApplied > 1) {
                workingApplied = {
                  ...workingApplied,
                  [evictKey]: { ...evicted, timesApplied: evicted.timesApplied - 1 },
                };
              } else {
                workingApplied = omit(workingApplied, evictKey);
              }
            }
          }
        }

        state.stagedCartPoints = stagedCartPoints + pointsToRestore - pointCost * quantity;

        const updatedAppliedRewards: IAppliedRewards = {
          ...workingApplied,
          [cartId]: {
            ...newReward,
            timesApplied: 1,
          },
        };
        updateAppliedRewardsInStorage(updatedAppliedRewards);
        state.appliedLoyaltyRewards = updatedAppliedRewards;
        return;
      }

      state.stagedCartPoints = stagedCartPoints - pointCost * quantity;

      const curAppliedTimes = appliedLoyaltyRewards[cartId]?.timesApplied || 0;

      const updatedAppliedRewards: IAppliedRewards = {
        ...appliedLoyaltyRewards,
        [cartId]: {
          ...newReward,
          timesApplied: curAppliedTimes + quantity,
        },
      };
      updateAppliedRewardsInStorage(updatedAppliedRewards);
      state.appliedLoyaltyRewards = updatedAppliedRewards;
    },
    createAvailableLoyaltyRewardsMap: (
      state,
      { payload }: PayloadAction<NonNullable<LoyaltyEngineRewards>>
    ) => {
      const availableRewardMap = payload.reduce((acc: AvailableRewards, reward) => {
        if (!reward?.rewardBenefitId) {
          return acc;
        }

        const rewardInMap = acc[reward.rewardBenefitId];
        // This ensures that the lowest point reward for the benefit item is displayed to the offer in the cart
        const rewardHasLowerPointCost = () => reward.pointCost < rewardInMap.pointCost;

        if (!rewardInMap || rewardHasLowerPointCost()) {
          acc[reward.rewardBenefitId] = reward;
        }

        return acc;
      }, {});

      state.availableLoyaltyRewardsMap = availableRewardMap;
    },
    rehydrateAppliedReward: state => {
      setStateFromAppliedRewards(state, getAppliedRewardsFromStorage());
      const stagedCartPoints = getStagedCartPoints(state, state.stagedCartPoints);
      if (isNumber(stagedCartPoints)) {
        state.stagedCartPoints = stagedCartPoints;
      }
    },
    removeAppliedReward: (state, { payload }: PayloadAction<IRewardAction>) => {
      const { cartId } = payload;
      const { appliedLoyaltyRewards, stagedCartPoints } = state;
      const { pointCost, timesApplied } = appliedLoyaltyRewards[cartId] || {};
      if (pointCost && timesApplied) {
        state.stagedCartPoints = stagedCartPoints + pointCost * timesApplied;
      }
      const updatedAppliedRewards = omit(appliedLoyaltyRewards, cartId);
      setStateFromAppliedRewards(state, updatedAppliedRewards);
    },
    removeAppliedRewards: (state, { payload }: PayloadAction<IRemoveAppliedRewardsAction>) => {
      const { cartIds } = payload;
      if (!cartIds.length) {
        return;
      }

      const { appliedLoyaltyRewards, stagedCartPoints } = state;

      // calculate the total points to be restored
      const restoredPoints = cartIds.reduce((pointsAccum: number, cartId: string) => {
        const { pointCost, timesApplied } = appliedLoyaltyRewards[cartId] || {};

        return pointCost && timesApplied ? pointsAccum + pointCost * timesApplied : pointsAccum;
      }, 0);

      state.stagedCartPoints = stagedCartPoints + restoredPoints;
      const updatedAppliedRewards = omit(appliedLoyaltyRewards, cartIds);
      setStateFromAppliedRewards(state, updatedAppliedRewards);
    },
    resetLoyaltyRewardsState: (state, { payload }: PayloadAction<IResetRewardStateAction>) => {
      const { appliedLoyaltyRewards } = state;
      const { points, shouldResetAvailableRewardsMap = true, redemptionMethod } = payload;

      const updatedAppliedRewards = pick(
        appliedLoyaltyRewards,
        // If set, discard only the rewards with the sent redemption method.
        // Check against `undefined` (not truthiness) so RewardRedemptionMethod.MOBILE (=== 0) is
        // honored as a real filter rather than being treated as "no method".
        // Normalize a stored reward's missing method to MOBILE, matching how every write path
        // defaults an omitted method. Without this, a legacy/persisted entry lacking the field
        // (undefined) survives a MOBILE clear (undefined !== 0), leaving a stale reward applied
        // after the mobile cart is emptied — still consuming points and showing as Applied.
        redemptionMethod !== undefined
          ? Object.keys(appliedLoyaltyRewards).filter(
              key =>
                (appliedLoyaltyRewards[key].redemptionMethod ?? RewardRedemptionMethod.MOBILE) !==
                redemptionMethod
            )
          : []
      );
      setStateFromAppliedRewards(state, updatedAppliedRewards);

      const appliedPoints = getAppliedPointsInCart(state);

      if (shouldResetAvailableRewardsMap) {
        state.availableLoyaltyRewardsMap = {};
      }

      // Prevent the value being 0
      state.stagedCartPoints = Math.max(points - appliedPoints, 0);
      state.shouldRefetchRewards = true;
    },
    setAvailableLoyaltyRewardsMap: (state, { payload }: PayloadAction<AvailableRewards | {}>) => {
      state.availableLoyaltyRewardsMap = payload;
    },
    setIsPricingRewardApplication: (state, { payload }: PayloadAction<boolean>) => {
      state.isPricingRewardApplication = payload;
    },
    setShouldRefetchRewards: (state, { payload }: PayloadAction<boolean>) => {
      state.shouldRefetchRewards = payload;
    },
    setStagedCartPoints: (state, { payload }: PayloadAction<number>) => {
      state.stagedCartPoints = payload;
    },
    setRewardsFeedbackMap: (state, { payload }: PayloadAction<IncentiveEvaluationMap>) => {
      state.rewardsFeedbackMap = payload;
    },
    unApplyReward: (
      state,
      // Not sure if this is the best way to handle loyalty user data. Maybe
      // there's a way to consume data from other slice
      { payload }: PayloadAction<IRewardAction & { loyaltyUser?: LoyaltyUser }>
    ) => {
      const { cartId, loyaltyUser } = payload;
      const { appliedLoyaltyRewards, stagedCartPoints } = state;
      const mappedReward = appliedLoyaltyRewards[cartId];

      if (!mappedReward) {
        return;
      }

      const { pointCost, rewardId, rewardBenefitId, redemptionMethod, sanityId } = mappedReward;

      // safety check for over subtracting
      const newBalanceExceedsUserBalance =
        stagedCartPoints + pointCost > (loyaltyUser?.points ?? 0);

      if (newBalanceExceedsUserBalance) {
        return;
      }

      state.stagedCartPoints = stagedCartPoints + pointCost;

      const curTimesApplied = appliedLoyaltyRewards[cartId]?.timesApplied || 0;
      const timesApplied = curTimesApplied - 1;
      const updatedAppliedRewards: IAppliedRewards =
        timesApplied > 0
          ? {
              ...appliedLoyaltyRewards,
              [cartId]: {
                timesApplied,
                rewardId,
                pointCost,
                rewardBenefitId,
                redemptionMethod,
                ...(sanityId ? { sanityId } : null),
              },
            }
          : omit(appliedLoyaltyRewards, cartId);
      setStateFromAppliedRewards(state, updatedAppliedRewards);
    },
  },
  extraReducers: {
    [SYNC_WITH_STORAGE_ACTION]: (state: IRewardsState) => {
      const appliedLoyaltyRewardsFromStorage = getAppliedRewardsFromStorage();
      if (!isEqual(state.appliedLoyaltyRewards, appliedLoyaltyRewardsFromStorage)) {
        state.appliedLoyaltyRewards = appliedLoyaltyRewardsFromStorage;
      }
    },
    [userSlice.actions.setUser.type]: (
      state: IRewardsState,
      { payload }: PayloadAction<LoyaltyUser | null>
    ) => {
      const loyaltyUserPoints = payload?.points ?? 0;
      const stagedCartPoints = getStagedCartPoints(state, loyaltyUserPoints);
      if (isNumber(stagedCartPoints)) {
        state.stagedCartPoints = stagedCartPoints;
      }
    },
  },
});

export function validateAppliedReward(
  availableLoyaltyRewardsMap: AvailableRewards,
  rewardBenefitId: string,
  totalTimesRewardApplied: Record<string, number>,
  quantity: number,
  stagedCartPoints: number
) {
  const mappedReward = availableLoyaltyRewardsMap[rewardBenefitId];
  if (!mappedReward) {
    return { isApplicable: false, mappedReward: null };
  }

  return {
    isApplicable: validateResolvedReward(
      mappedReward,
      totalTimesRewardApplied,
      quantity,
      stagedCartPoints
    ),
    mappedReward,
  };
}

export function validateResolvedReward(
  reward: { id: string; pointCost: number; limitPerOrder?: number | null },
  totalTimesRewardApplied: Record<string, number>,
  quantity: number,
  stagedCartPoints: number
) {
  const { pointCost, id: rewardId, limitPerOrder } = reward;

  const timesApplied = totalTimesRewardApplied[rewardId];
  // safety check for over-redemption
  if (
    pointCost * quantity > stagedCartPoints ||
    (typeof limitPerOrder === 'number' && timesApplied === limitPerOrder)
  ) {
    return false;
  }

  return true;
}

export function canApplyReward({
  reward,
  appliedLoyaltyRewards,
  cartId,
  quantity,
  stagedCartPoints,
  enableRewardUnstacking,
  rewardRedemptionLimit,
}: {
  reward: { id: string; pointCost: number; limitPerOrder?: number | null };
  appliedLoyaltyRewards: IAppliedRewards;
  cartId: string;
  quantity: number;
  stagedCartPoints: number;
  enableRewardUnstacking: boolean;
  rewardRedemptionLimit: number;
}) {
  // A swap or eviction restores points and applications before the incoming reward is validated.
  let effectivePoints = stagedCartPoints;
  let effectiveApplications = computeTotalTimesRewardApplied(appliedLoyaltyRewards);

  if (enableRewardUnstacking) {
    const currentAtCartId = appliedLoyaltyRewards[cartId];

    if (currentAtCartId && currentAtCartId.rewardId !== reward.id) {
      effectivePoints += currentAtCartId.pointCost * currentAtCartId.timesApplied;
      effectiveApplications = {
        ...effectiveApplications,
        [currentAtCartId.rewardId]: Math.max(
          0,
          (effectiveApplications[currentAtCartId.rewardId] ?? 0) - currentAtCartId.timesApplied
        ),
      };
    } else if (
      !currentAtCartId &&
      sumAppliedTimesApplied(appliedLoyaltyRewards) >= rewardRedemptionLimit
    ) {
      const evicted = appliedLoyaltyRewards[Object.keys(appliedLoyaltyRewards)[0]];
      if (evicted) {
        effectivePoints += evicted.pointCost;
        effectiveApplications = {
          ...effectiveApplications,
          [evicted.rewardId]: Math.max(0, (effectiveApplications[evicted.rewardId] ?? 0) - 1),
        };
      }
    }
  }

  return validateResolvedReward(reward, effectiveApplications, quantity, effectivePoints);
}
